import { Logger, LoggerService } from "@Shared/logger";
import { AUTH0_CONST, Auth0Service, AuthProfile, GetTokenSilentlyCacheModes } from "@Modules/auth_module";
import { PipelineContext, PipelineStep, StepResult } from "@Modules/pipeline_module/_base";
import { Bus } from "@Modules/event_module";
import { APP_EVENTS } from "@Core";
import { HandleCallbackRedirectStep, NotAuthenticatedStep } from ".";
import { UrlUtil } from "@Shared/utils";
import { AuthenticationUtils } from "@Modules/auth_module/utils/auth.utils";
import { AppleService } from "@Modules/apple_module";
import { AppleCallbackStep } from "../apple/apple-callback.step";
import {Auth0AuthenticatedStep} from "@Modules/pipeline_module/steps/auth/auth0-authenticated-step";
import {SessionService} from "@Modules/session_module/services/session.service";


export class CheckAuthStatusStep implements PipelineStep {
    private auth0Service: Auth0Service;
    private Logger: Logger = LoggerService.createLogger("CheckAuthStatusStep");

    
    constructor() {
        this.Logger.trace("****  CTOR ****");
        
    }
    async execute(context: PipelineContext): Promise<PipelineStep | null> {
        this.Logger.trace("Executing....", context);
        this.auth0Service = Auth0Service.Instance;
        
        Bus.emit(APP_EVENTS.onAuthStarted);
        try {
            const res = await this.auth0Service.getAuthState();
            this.Logger.trace("Auth State Result", res);
            context.checkAuthStatus = { isAuthenticated: res.isAuthenticated, user: res.AuthProfile };

            // The Auth0 SDK only knows about its own local session. On a cross-domain login the
            // SDK may have no session even though a valid one exists on the session server __MNG_Session.
            if (!context.checkAuthStatus.isAuthenticated) {
                try {
                    const session = await SessionService.Instance.getAuth0Session();
                    if (session.isAuthenticated) {
                        this.Logger.trace("Recovered session from session server / __MNG_Session", session);
                        context.checkAuthStatus = { isAuthenticated: true, user: session.AuthProfile };
                    }
                } catch (sessionError) {
                    this.Logger.warn("Session fallback check failed", sessionError);
                }
            }
        } catch (error) {
            context.checkAuthStatus = { isAuthenticated: false, error: error.message };
        }

        Bus.emitAndTrack(APP_EVENTS.onAuthDetermined, context.checkAuthStatus, 'authState');
        this.Logger.trace("isAuthenticated", context.checkAuthStatus.isAuthenticated);

        if (context.checkAuthStatus.isAuthenticated && window.location.pathname.startsWith('/login')) {
            const authRedirect = UrlUtil.getQueryParam(AUTH0_CONST.authRedirectParam);
            let target = authRedirect ? authRedirect : window.location.origin;
            if (target.endsWith('#')) {
                target = target.slice(0, -1);
            }
            this.Logger.trace("Already authenticated on /login, redirecting to", target);
            window.location.assign(target);
            return null;
        }

        // fire 'authenticationReady' event to play nice with legacy CMS functionality
        await AuthenticationUtils.fireLegacyAuthenticationReadyEvent(this.auth0Service.AuthClient);

        return context.checkAuthStatus.isAuthenticated ? new Auth0AuthenticatedStep() : new NotAuthenticatedStep();
    }
}

/**
 * On /callback with an `auth_redirect` param the pipeline must navigate the user back to
 * the original destination. Returns AppleCallbackStep for the Apple News round-trip (which
 * resolves the encrypted uuid before delegating to the redirect step), HandleCallbackRedirectStep
 * for any other auth_redirect, or null when the current page has no redirect to perform.
 */
export function buildPostCallbackStep(): PipelineStep | null {
    if (!window.location.pathname.startsWith('/callback')) return null;
    if (!UrlUtil.getQueryParam(AUTH0_CONST.authRedirectParam)) return null;
    if (AppleService.Instance.isPostAppleCallback) return new AppleCallbackStep();
    return new HandleCallbackRedirectStep();
}
export type CheckAuthStatusStepResultData = {
    isAuthenticated: boolean;
    cacheMode?: GetTokenSilentlyCacheModes;
    AuthProfile?: AuthProfile;
}
//TODO: use this instead in the authService.
export type CheckAuthStatusStepResult = StepResult<CheckAuthStatusStepResultData>;